/// <amd-module name="TFS/DRITools/Components/UsageInsights/UsageView" />
import * as d3 from "d3";
import * as React from "react";

// Office Fabric
import { IColumn } from "OfficeFabric/DetailsList";
import { Label } from "OfficeFabric/Label";
import { SelectionMode } from "OfficeFabric/Selection";
import { GraphSubjectBase } from "VSS/Platform/Contracts/Graph";
// VSS
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { TextField } from "VSSUI/TextField";
import { Toggle } from "VSSUI/Toggle";
import { Tooltip } from "VSSUI/TooltipEx";

// VSSUI
import { IVssPageService } from "VSS/Platform/Page";
import { IPickListItem, IPickListSelection, PickList } from "VSSUI/PickList";
import { VssDetailsList } from "VSSUI/VssDetailsList";
import { VssIconType } from "VSSUI/VssIcon";

// Resources
import * as Resources from "TFS/DRITools/Resources";

// DRITools
import { ErrorMessage } from "TFS/DRITools/Components/Common/Error";
import { Loading } from "TFS/DRITools/Components/Common/Loading";
import { IdentityForm } from "TFS/DRITools/Components/UsageInsights/IdentityForm";
import { TimeRangeForm } from "TFS/DRITools/Components/UsageInsights/TimeRangeForm";
import { Visualizations } from "TFS/DRITools/Components/UsageInsights/Visualizations";
import { IDRIToolsRestClient } from "TFS/DRITools/RestClient/DRITools";
import { DataInfo, Line } from "TFS/DRITools/util/RawDataTypes";
import { Obj } from "TFS/DRITools/util/RequestNode";

interface HostInfo {
    hostId: string;
    isHosted: boolean;
    hostPath: string;
    userId: string;
    name: string;
}

interface Graph {
    graph: Line[];
    info: DataInfo;
    chosenLines: string[];
}

interface Table {
    table: Obj[];
    info: DataInfo;
    columns: IColumn[];
}

export interface IUsageViewState extends IVssComponentProperties {
    service: string;
    userId: string;
    loading: boolean;
    error: string;
    graph?: Graph;
    throttleReason?: Table;
    atCPUCommands?: Table;
    dbCPUCommands?: Table;
    useVSID: boolean;
}

class UsageView extends VssComponent<{}, IUsageViewState> {
    private _driToolClient: IDRIToolsRestClient;
    private _allColumns: string[];
    private _color: d3.ScaleOrdinal<string, string>;
    private _hostInfo: HostInfo;
    private _identityComponent: IdentityForm | null;
    private _timeRangeComponent: TimeRangeForm | null;

    private _inputChange = {
        chosenLines: (selection: IPickListSelection): void => {
            const newData: Graph = { ...this.state.graph! };
            newData.chosenLines = selection.selectedItems;
            this.setState({ graph: newData });
        },
        vsid: (vsid: string): void => {
            this.setState({ userId: vsid });
        },
        toggleInput: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, checked: boolean): void => {
            this.setState({ useVSID: checked });
        },
        service: (service: string): void => {
            this.setState({ service: service });
        }
    };

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);
        this._hostInfo = {
            hostId: "",
            isHosted: false,
            hostPath: "",
            userId: "",
            name: ""
        };
        this._allColumns = [];
        const vssPageService = context.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();
        if (vssPageData) {
            this._hostInfo.hostId = vssPageData.hostId;
            this._hostInfo.isHosted = vssPageData.isHosted;
            this._hostInfo.hostPath = vssPageData.hostPath;
            this._hostInfo.userId = vssPageData.user.id;
            this._hostInfo.name = (vssPageData.user as GraphSubjectBase).displayName;
        }

        this._color = d3.scaleOrdinal(d3.schemeCategory10);

        this._driToolClient = this.context.pageContext.getRestClient<IDRIToolsRestClient>("IDRIToolsRestClient");
        this.state = {
            service: "tfs",
            userId: "",
            loading: false,
            error: "",
            useVSID: false
        };
    }

    public render(): JSX.Element {
        const { loading, error, userId, useVSID, service, graph, throttleReason, atCPUCommands, dbCPUCommands } = this.state;

        const endTime = new Date();
        const startTime = new Date(endTime.getFullYear(), endTime.getMonth(), endTime.getDate(), endTime.getHours() - 1, endTime.getMinutes());

        return (
            <div className="usage-hub">
                <form onSubmit={this._getData} className="usage-form">
                    <div className="flex-row">
                        <div className="flex-column form-item time-range">
                            <TimeRangeForm ref={ref => (this._timeRangeComponent = ref)} startTime={startTime} endTime={endTime} />
                        </div>
                        <div className="flex-column form-item">
                            <Label>{Resources.UsageInsights_Service}</Label>
                            <TextField
                                className="service-form"
                                value={service}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>, newValue: string) => this._inputChange.service(newValue)}
                                readOnly={false}
                            />
                        </div>

                        <div className="flex-column form-item toggle">
                            <Toggle checked={useVSID} text={Resources.UsageInsights_CustomVSID} onChange={this._inputChange.toggleInput} />
                        </div>

                        {useVSID ? (
                            <div className="flex-column form-item">
                                <Label>{Resources.UsageInsights_Label_TextInput}</Label>
                                <TextField
                                    className="id-form"
                                    required={useVSID}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>, newValue: string) => this._inputChange.vsid(newValue)}
                                    readOnly={false}
                                    value={userId}
                                />
                            </div>
                        ) : (
                            <div className="flex-column form-item justify-end identity-picker">
                                <IdentityForm initialSearch={this._hostInfo.name} ref={ref => (this._identityComponent = ref)} />
                            </div>
                        )}
                    </div>
                    <div className="flex-row form-item form-button">
                        <Button primary={true} type="submit" text={Resources.UsageInsights_PrimaryButtonText} />
                    </div>
                </form>
                <div className="flex flex-column">
                    {loading ? (
                        <Loading />
                    ) : error !== "" ? (
                        <ErrorMessage error={error} />
                    ) : (
                        <div>
                            {throttleReason && this._createTable(throttleReason)}
                            {(atCPUCommands || dbCPUCommands) && (
                                <div className="flex flex-row">
                                    {atCPUCommands && this._createTable(atCPUCommands)}
                                    <div className={atCPUCommands ? "dbCPU" : ""}>{dbCPUCommands && this._createTable(dbCPUCommands)}</div>
                                </div>
                            )}
                            {graph && this._createGraph(graph)}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    public componentDidMount(): void {
        window.addEventListener("resize", () => {
            this.forceUpdate();
        });
    }

    public componentWillUnmount(): void {
        window.addEventListener("resize", () => {});
    }

    private _getData = async (event: React.MouseEvent<HTMLElement> | Event | React.FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();

        this.setState({
            loading: true,
            error: "",
            graph: undefined
        });

        let { userId } = this.state;
        const { service, useVSID } = this.state;

        let startTime!: Date;
        let endTime!: Date;
        if (this._timeRangeComponent) {
            startTime = this._timeRangeComponent.getStartTime();
            endTime = this._timeRangeComponent.getEndTime();
        }

        if (!useVSID && this._identityComponent) {
            userId = this._identityComponent.getIdentity().id;
        }
        if (userId === "") {
            this.setState({ loading: false, error: "VSID cannot be empty" });
            return;
        }

        this._driToolClient
            .getUsageInsights(userId, startTime, endTime, service)
            .then(
                (results: Obj[]): void => {
                    if (results.length === 0) {
                        throw new Error("No data for this query.");
                    }
                    this._allColumns = [];
                    const initialColumns: string[] = [];
                    let usage: Graph | undefined = undefined;
                    let throttleReason: Table | undefined = undefined;
                    let atCPUCommands: Table | undefined = undefined;
                    let dbCPUCommands: Table | undefined = undefined;
                    while (results.length !== 0) {
                        const info = results[0].pop() as DataInfo;
                        if (info.type === "graph") {
                            if (usage != undefined || throttleReason == undefined) {
                                throw new Error("Unexpected graph from API.");
                            }
                            const graph = results.shift() as Line[];
                            graph.forEach(line => {
                                line.color = this._color(line.key);
                                this._allColumns.push(line.key);
                            });
                            usage = { graph: graph, info: info, chosenLines: initialColumns };
                        } else {
                            const table = results.shift() as Obj[];
                            const columns: IColumn[] = [];
                            Object.keys(table[0]).map((col: string) => {
                                columns.push({
                                    key: col,
                                    fieldName: col,
                                    name: col,
                                    onRender: (item: any) => {
                                        const text: string | number = item[col];
                                        return (
                                            <Tooltip text={text.toString()}>
                                                <span className={col === Resources.UsageInsights_NumBlocked && text > 0 ? "blocked-highlight" : ""}>
                                                    {text}
                                                </span>
                                            </Tooltip>
                                        );
                                    },
                                    minWidth: 796 / Object.keys(table[0]).length - 16,
                                    isSortedDescending: col === "Total" ? true : false,
                                    isMultiline: true
                                });
                            });
                            if (info.name == Resources.UsageInsights_ThrottleReason) {
                                throttleReason = { table: table, info: info, columns: columns };
                                if ((info.description as any).includes(Resources.UsageInsights_CPUCycles)) {
                                    initialColumns.push(Resources.UsageInsights_ATCPU5min, Resources.UsageInsights_ATCPU1hr);
                                }
                                if ((info.description as any).includes(Resources.UsageInsights_XEventCPUTime)) {
                                    initialColumns.push(Resources.UsageInsights_DBCPU5min, Resources.UsageInsights_DBCPU1hr);
                                }
                                if ((info.description as any).includes(Resources.UsageInsights_Concurrency) || initialColumns.length == 0) {
                                    initialColumns.push(Resources.UsageInsights_MaxConcurrency, Resources.UsageInsights_MaxConcurrencySemaphoreTime);
                                }
                                initialColumns.push(Resources.UsageInsights_NumBlockedRequests, Resources.UsageInsights_NumDelayedRequests);
                            } else if (info.name == Resources.UsageInsights_ATCPU) {
                                atCPUCommands = { table: table, info: info, columns: columns };
                            } else if (info.name == Resources.UsageInsights_DBCPU) {
                                dbCPUCommands = { table: table, info: info, columns: columns };
                            } else {
                                throw new Error("Unexpected table from API.");
                            }
                        }
                    }
                    this.setState({
                        loading: false,
                        error: "",
                        graph: usage,
                        throttleReason: throttleReason,
                        atCPUCommands: atCPUCommands,
                        dbCPUCommands: dbCPUCommands
                    });
                }
            )
            .catch(
                (err): void => {
                    this.setState({ loading: false, error: err });
                }
            );
    };

    private _createTable(table: Table): JSX.Element {
        return (
            <div className="usage-table" key={table.info.name}>
                <div className="flex flex-center title form-item">
                    <strong>{table.info.name}</strong>
                </div>
                {table.info.name !== Resources.UsageInsights_ThrottleReason ? (
                    <div className="flex flex-center title form-item">
                        <p>{table.info.description}</p>
                    </div>
                ) : null}
                <div className="table">
                    <VssDetailsList
                        items={table.table}
                        selectionPreservedOnEmptyClick={false}
                        selectionMode={SelectionMode.none}
                        columns={table.columns}
                    />
                </div>
            </div>
        );
    }

    private _createGraph(graph: Graph): JSX.Element {
        return (
            <div key={graph.info.name} className="graph">
                <div className="flex flex-center title form-item">
                    <strong>{graph.info.name}</strong>
                </div>
                <div className="flex flex-center title form-item">
                    <p>{graph.info.description}</p>
                </div>
                <div className="flex flex-center">
                    <div className="flex-column">
                        <Visualizations
                            data={graph.graph}
                            width={window.innerWidth - 570}
                            height={550}
                            graphInfo={graph.info}
                            chosenLines={graph.chosenLines}
                        />
                    </div>
                    <div className="flex-column">
                        <PickList
                            items={this._allColumns}
                            onSelectionChanged={(selection: IPickListSelection) => {
                                this._inputChange.chosenLines(selection);
                            }}
                            isSearchable={false}
                            initiallySelectedItems={graph.chosenLines}
                            showSelectAll={true}
                            getListItem={(item: string): IPickListItem => {
                                return {
                                    key: item,
                                    name: item,
                                    iconProps: {
                                        iconName: "CircleFill",
                                        iconType: VssIconType.fabric,
                                        styles: { root: { color: this._color(item) } }
                                    }
                                };
                            }}
                            selectionMode={SelectionMode.multiple}
                            className="picklist-color"
                        />
                    </div>
                </div>
            </div>
        );
    }
}

export default UsageView;
